import React, { createContext, useState, useEffect, useContext } from 'react';

const AccessibilityContext = createContext();

export const useAccessibility = () => useContext(AccessibilityContext);

export const AccessibilityProvider = ({ children }) => {
  // --- ESTADOS EXISTENTES ---
  const [fontSizeLevel, setFontSizeLevel] = useState(() => parseInt(localStorage.getItem('access_fontSize')) || 0);
  const [highContrast, setHighContrast] = useState(() => localStorage.getItem('access_contrast') === 'true');
  const [dyslexiaFont, setDyslexiaFont] = useState(() => localStorage.getItem('access_dyslexia') === 'true');
  const [spacingLevel, setSpacingLevel] = useState(() => parseInt(localStorage.getItem('access_spacing')) || 0);
  const [monochrome, setMonochrome] = useState(() => localStorage.getItem('access_mono') === 'true');
  const [bigCursor, setBigCursor] = useState(() => localStorage.getItem('access_cursor') === 'true');
  const [hideImages, setHideImages] = useState(() => localStorage.getItem('access_hideImg') === 'true');

  // --- NOVOS ESTADOS (V3.0) ---
  const [readingGuide, setReadingGuide] = useState(() => localStorage.getItem('access_guide') === 'true');
  const [highlightLinks, setHighlightLinks] = useState(() => localStorage.getItem('access_links') === 'true');
  const [pauseAnim, setPauseAnim] = useState(() => localStorage.getItem('access_pause') === 'true');
  const [invertColors, setInvertColors] = useState(() => localStorage.getItem('access_invert') === 'true');

  // --- EFEITO PRINCIPAL ---
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    // 1. Tamanho da Fonte
    const newPercentage = 100 + (fontSizeLevel * 10);
    root.style.fontSize = `${newPercentage}%`;
    localStorage.setItem('access_fontSize', fontSizeLevel);

    // 2. Classes CSS (Toggle Geral)
    const toggleClass = (condition, className) => {
      if (condition) body.classList.add(className);
      else body.classList.remove(className);
    };

    toggleClass(highContrast, 'high-contrast-mode');
    localStorage.setItem('access_contrast', highContrast);

    toggleClass(dyslexiaFont, 'dyslexia-mode');
    localStorage.setItem('access_dyslexia', dyslexiaFont);

    body.classList.remove('spacing-1', 'spacing-2');
    if (spacingLevel > 0) body.classList.add(`spacing-${spacingLevel}`);
    localStorage.setItem('access_spacing', spacingLevel);

    toggleClass(monochrome, 'monochrome-mode');
    localStorage.setItem('access_mono', monochrome);

    toggleClass(bigCursor, 'big-cursor-mode');
    localStorage.setItem('access_cursor', bigCursor);

    toggleClass(hideImages, 'hide-images-mode');
    localStorage.setItem('access_hideImg', hideImages);

    // --- APLICAÇÃO DAS NOVAS FUNÇÕES ---
    
    toggleClass(readingGuide, 'reading-guide-mode');
    localStorage.setItem('access_guide', readingGuide);

    toggleClass(highlightLinks, 'highlight-links-mode');
    localStorage.setItem('access_links', highlightLinks);

    toggleClass(pauseAnim, 'pause-anim-mode');
    localStorage.setItem('access_pause', pauseAnim);

    toggleClass(invertColors, 'invert-colors-mode');
    localStorage.setItem('access_invert', invertColors);

  }, [fontSizeLevel, highContrast, dyslexiaFont, spacingLevel, monochrome, bigCursor, hideImages, readingGuide, highlightLinks, pauseAnim, invertColors]);

  // --- EFEITO ESPECIAL PARA A GUIA DE LEITURA (Seguir o Mouse) ---
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (readingGuide) {
        document.documentElement.style.setProperty('--guide-y', `${e.clientY}px`);
      }
    };

    if (readingGuide) {
      window.addEventListener('mousemove', handleMouseMove);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      document.documentElement.style.removeProperty('--guide-y');
    }

    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [readingGuide]);

  // --- FUNÇÕES DE CONTROLE ---
  const resetSettings = () => {
    setFontSizeLevel(0);
    setHighContrast(false);
    setDyslexiaFont(false);
    setSpacingLevel(0);
    setMonochrome(false);
    setBigCursor(false);
    setHideImages(false);
    setReadingGuide(false);
    setHighlightLinks(false);
    setPauseAnim(false);
    setInvertColors(false);
  };

  return (
    <AccessibilityContext.Provider
      value={{
        fontSizeLevel, setFontSizeLevel,
        highContrast, toggleHighContrast: () => setHighContrast(p => !p),
        dyslexiaFont, toggleDyslexiaFont: () => setDyslexiaFont(p => !p),
        spacingLevel, toggleSpacing: () => setSpacingLevel(p => (p === 2 ? 0 : p + 1)),
        monochrome, toggleMonochrome: () => setMonochrome(p => !p),
        bigCursor, toggleBigCursor: () => setBigCursor(p => !p),
        hideImages, toggleHideImages: () => setHideImages(p => !p),
        // Novos Controles
        readingGuide, toggleReadingGuide: () => setReadingGuide(p => !p),
        highlightLinks, toggleHighlightLinks: () => setHighlightLinks(p => !p),
        pauseAnim, togglePauseAnim: () => setPauseAnim(p => !p),
        invertColors, toggleInvertColors: () => setInvertColors(p => !p),
        resetSettings
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};